import type { Dayjs } from 'dayjs';
import type { BadgeProps } from '@mui/material';
import type { AccountList } from 'src/types/user';
import type { Theme, SxProps } from '@mui/material/styles';

import React, { forwardRef } from 'react';

import Badge from '@mui/material/Badge';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import { styled } from '@mui/material/styles';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';

import { HeaderCell } from './head-header-cell';
import { FilterPopover } from './filters/head-filter-popover';

import type { TableHeadCellProps } from './table-head-custom';

// ----------------------------------------------------------------------

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  left: '-7px !important',
  '& .MuiBadge-badge': {
    top: 12,
    padding: '0 4px',
  },
}));

// ----------------------------------------------------------------------

type TableHeadViewProps = {
  orderBy?: string;
  rowCount?: number;
  sx?: SxProps<Theme>;
  numSelected?: number;
  order?: 'asc' | 'desc';
  headCells: TableHeadCellProps[];
  onSort?: (id: string) => void;
  onSelectAllRows?: (checked: boolean) => void;
  handleFilterChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    column: keyof AccountList
  ) => void;
  selectRowPremission?: boolean;
  sortFldList?: string[];
  isCampaignTamplate?: boolean;
  isEdit?: boolean;
  isDelete?: boolean;
  setDateTimeFilter?: any;
  isCampaining?: boolean;
  handleToggleSelectAll?: any;
  selectAllFilter?: any;
  count?: number;
  defaultBedgecondition?: boolean;
  selectedContact?: any;
  dateTimeFiltre: {
    id: string;
    fromDate: Dayjs;
    toDate: Dayjs;
  }[];
  setPage: any;
  moduleName?: string;
  resetFilterKey?: number;
  // Props from logic hook
  filterState: any;
  filterHandlers: any;
  externalData: any;
  commonProps: any;
  disableSelectAll?: boolean;
};

// ----------------------------------------------------------------------

export const TableHeadView = forwardRef<{ handleRemoveAllFilter: () => void }, TableHeadViewProps>(
  (
    {
      sx,
      order,
      onSort,
      orderBy,
      headCells,
      rowCount = 0,
      numSelected = 0,
      onSelectAllRows,
      selectRowPremission = true,
      sortFldList,
      isCampaignTamplate = false,
      isEdit = false,
      isDelete = false,
      isCampaining = false,
      handleToggleSelectAll = () => {},
      selectAllFilter,
      count = 0,
      defaultBedgecondition,
      selectedContact,
      moduleName = '',
      filterState,
      filterHandlers,
      externalData,
      commonProps,
      disableSelectAll = false,
    },
    ref
  ) => {
    const { t, modifiedTypes } = commonProps;
    const {
      anchorEl,
      selectedColumn,
      filterValue,
      setFilterValue,
      fromDate,
      toDate,
      setFromDate,
      setToDate,
      dateFieldColumns,
    } = filterState;
    const {
      handleOpenPopover,
      handleClosePopover,
      handleApplyFilter,
      handleResetFilter,
      isFilterApplied,
    } = filterHandlers;

    return (
      <>
        <TableHead sx={sx} className="myTableHead">
          <TableRow>
            {/* Selection checkbox */}
            {selectRowPremission && onSelectAllRows && (
              <TableCell padding="checkbox">
                {!isCampaining ? (
                  <Checkbox
                    indeterminate={numSelected > 0 && numSelected < rowCount}
                    checked={rowCount > 0 && numSelected === rowCount}
                    onChange={(event) => onSelectAllRows(event.target.checked)}
                    inputProps={{
                      id: 'all-row-checkbox',
                      'aria-label': 'Select all rows',
                    }}
                  />
                ) : (
                  !isCampaignTamplate && (
                    <StyledBadge
                      color="primary"
                      badgeContent={
                        selectAllFilter.active
                          ? selectAllFilter.filterType === 'not in'
                            ? count - selectAllFilter.ids.length
                            : defaultBedgecondition && selectedContact
                              ? selectedContact?.length
                              : count
                          : selectAllFilter.filterType === 'in'
                            ? selectAllFilter.ids.length
                            : null
                      }
                      invisible={!selectAllFilter.active && selectAllFilter.ids.length === 0}
                    >
                      <Checkbox
                        indeterminate={selectAllFilter.active && selectAllFilter.ids.length > 0}
                        checked={selectAllFilter.active && selectAllFilter.ids.length === 0}
                        onChange={handleToggleSelectAll}
                        disabled={disableSelectAll}
                        inputProps={{
                          id: 'all-row-checkbox',
                          'aria-label': 'Select all rows',
                        }}
                      />
                    </StyledBadge>
                  )
                )}
              </TableCell>
            )}

            {/* Header cells */}
            {headCells.map((headCell) => {
              const isActionColumn = ['action', 'resendMail'].includes(headCell.id);
              const updateCondition =
                moduleName === 'TENANTS' ? isEdit && isDelete : isEdit || isDelete;
              const showActionHeader =
                moduleName === 'LEARNING_QUEUE' ||
                moduleName === 'KNOWLEDGE_GAPS' ||
                moduleName === 'CSAT' ||
                moduleName === 'CSAT_AGENT_SCORES'
                  ? true
                  : updateCondition;

              if (isActionColumn && showActionHeader) {
                return (
                  <TableCell
                    key={headCell.id}
                    align={headCell.align || 'left'}
                    sx={{ width: headCell.width, ...headCell.sx }}
                  >
                    {headCell.label}
                  </TableCell>
                );
              }

              if (!isActionColumn) {
                return (
                  <HeaderCell
                    key={headCell.id}
                    headCell={headCell}
                    order={order}
                    orderBy={orderBy}
                    onSort={onSort}
                    sortFldList={sortFldList}
                    isFilterApplied={isFilterApplied(headCell.id)}
                    onFilterClick={handleOpenPopover}
                  />
                );
              }

              return null;
            })}
          </TableRow>
        </TableHead>

        {/* Filter Popover */}
        <FilterPopover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          selectedColumn={selectedColumn}
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          fromDate={fromDate}
          toDate={toDate}
          setFromDate={setFromDate}
          setToDate={setToDate}
          dateFieldColumns={dateFieldColumns}
          onApply={handleApplyFilter}
          onReset={handleResetFilter}
          externalData={externalData}
          t={t}
          modifiedTypes={modifiedTypes}
        />
      </>
    );
  }
);
